import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Toaster, toast } from 'sonner';
import {
  ArrowLeft,
  LogOut,
  Heart,
  MapPin,
  Calendar,
  CheckCircle2,
  Clock,
  User,
  TrendingUp,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatRupiah = (amount) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount || 0);

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
};

const PRAYER_LABEL = { Fajr: 'Subuh', Dhuhr: 'Dzuhur', Asr: 'Ashar', Maghrib: 'Maghrib', Isha: 'Isya', Sholat: 'Sholat' };

const JamaahDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [donations, setDonations] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('donasi');

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [donRes, attRes] = await Promise.all([
          axios.get(`${API_URL}/api/user/donations`, { withCredentials: true }),
          axios.get(`${API_URL}/api/user/attendance`, { withCredentials: true }),
        ]);
        setDonations(donRes.data || []);
        setAttendance(attRes.data || []);
      } catch (err) {
        toast.error('Gagal memuat data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const totalDonasi = donations.reduce((s, d) => s + (d.amount_display || 0), 0);
  const totalAbsen = attendance.length;

  // Group attendance by mosque
  const absenByMosque = attendance.reduce((acc, a) => {
    const key = a.mosque_id || 'unknown';
    if (!acc[key]) acc[key] = { mosque_id: key, count: 0, records: [] };
    acc[key].count++;
    acc[key].records.push(a);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-900 text-white">
        <div className="max-w-2xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <button
              data-testid="jamaah-back-btn"
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-body font-medium text-white/70">Dasbor Jamaah</span>
            <button
              data-testid="jamaah-logout-btn"
              onClick={handleLogout}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              title="Keluar"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          {/* Profile */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 border-2 border-white/40 overflow-hidden flex items-center justify-center flex-shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-xl font-heading font-bold text-white" data-testid="jamaah-name">
                {user?.name || 'Jamaah'}
              </h1>
              <p className="text-sm text-white/70">{user?.email}</p>
              <span className="inline-block mt-1 text-xs bg-amber-400/20 text-amber-300 rounded-full px-3 py-0.5 font-medium border border-amber-400/30">
                Jamaah Masjid Berdaya
              </span>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20"
              data-testid="jamaah-total-donasi"
            >
              <div className="flex items-center gap-2 mb-1">
                <Heart className="w-4 h-4 text-amber-300" />
                <span className="text-xs text-white/70">Total Donasi</span>
              </div>
              <p className="text-lg font-bold text-white">{formatRupiah(totalDonasi)}</p>
              <p className="text-xs text-white/50">{donations.length} transaksi</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20"
              data-testid="jamaah-total-absen"
            >
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-amber-300" />
                <span className="text-xs text-white/70">Total Absen</span>
              </div>
              <p className="text-lg font-bold text-white">{totalAbsen}</p>
              <p className="text-xs text-white/50">waktu sholat</p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-6">
        {/* Tab Selector */}
        <div className="bg-white rounded-2xl border border-stone-200 p-1.5 flex gap-1 mb-6">
          <button
            data-testid="jamaah-tab-donasi"
            onClick={() => setActiveTab('donasi')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-body font-medium transition-colors ${
              activeTab === 'donasi' ? 'bg-emerald-700 text-white' : 'text-stone-600 hover:bg-stone-50'
            }`}
          >
            <Heart className="w-4 h-4" />
            Riwayat Sedekah
          </button>
          <button
            data-testid="jamaah-tab-absen"
            onClick={() => setActiveTab('absen')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-body font-medium transition-colors ${
              activeTab === 'absen' ? 'bg-emerald-700 text-white' : 'text-stone-600 hover:bg-stone-50'
            }`}
          >
            <MapPin className="w-4 h-4" />
            Presensi Sholat
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-200 border-t-emerald-700" />
          </div>
        ) : activeTab === 'donasi' ? (
          <div data-testid="jamaah-donasi-list">
            {donations.length === 0 ? (
              <div className="text-center py-16">
                <Heart className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                <p className="text-stone-500 font-body">Belum ada riwayat donasi</p>
                <p className="text-xs text-stone-400 mt-1">Mulai berinfaq atau zakat melalui aplikasi masjid terdekat</p>
              </div>
            ) : (
              <div className="space-y-3">
                {donations.map((d, i) => (
                  <motion.div
                    key={d.id || i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="bg-white rounded-2xl border border-stone-100 p-4 flex items-start gap-4"
                    data-testid={`donasi-item-${i}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      d.donation_type === 'Zakat' ? 'bg-amber-50' : 'bg-emerald-50'
                    }`}>
                      {d.donation_type === 'Zakat' ? (
                        <TrendingUp className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Heart className="w-5 h-5 text-emerald-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-medium rounded-full px-2.5 py-0.5 ${
                          d.donation_type === 'Zakat'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}>
                          {d.donation_type}
                        </span>
                        <span className="text-sm font-bold text-emerald-800">{formatRupiah(d.amount_display)}</span>
                      </div>
                      <p className="text-sm font-body text-stone-700 mt-1 truncate">
                        {d.donor_name || user?.name || 'Hamba Allah'}
                      </p>
                      {d.ticket_codes && d.ticket_codes.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {d.ticket_codes.slice(0, 3).map((code) => (
                            <span key={code} className="text-[10px] font-mono bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">
                              {code}
                            </span>
                          ))}
                          {d.ticket_codes.length > 3 && (
                            <span className="text-[10px] text-stone-400">+{d.ticket_codes.length - 3} tiket</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-1.5">
                        <Calendar className="w-3 h-3 text-stone-400" />
                        <span className="text-xs text-stone-400">{formatDate(d.created_at)}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div data-testid="jamaah-absen-list">
            {attendance.length === 0 ? (
              <div className="text-center py-16">
                <MapPin className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                <p className="text-stone-500 font-body">Belum ada riwayat absen</p>
                <p className="text-xs text-stone-400 mt-1">Login dan absen sholat di masjid terdekat (radius 50m)</p>
              </div>
            ) : (
              <div className="space-y-3">
                {attendance.map((a, i) => (
                  <motion.div
                    key={a.id || i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center gap-4"
                    data-testid={`absen-item-${i}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-stone-800">
                          {PRAYER_LABEL[a.prayer] || a.prayer}
                        </span>
                        <span className="text-xs bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-0.5 font-medium">
                          {a.distance_m ? `${a.distance_m}m` : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-xs text-stone-400">
                          <Calendar className="w-3 h-3" />
                          {formatDate(a.date || a.created_at)}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JamaahDashboard;
